import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, ToastController } from '@ionic/angular';
import { combineLatest, of, switchMap, takeUntil, map, distinctUntilChanged } from 'rxjs';
import { OnOff, RoomName } from 'src/graphql/generated/schema';
import { CommonPage } from '../../common-page/common-page';
import { UserSettingsService } from 'src/graphql/services/user-settings.service';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';
import { IShadowState } from 'src/mqtt/interfaces/interfaces';
import { actionId, actionType } from 'src/mqtt/actions-list';
import { getActionKey } from 'src/mqtt/utils/device-name-parser';
import { ClimateControlService } from '../../services/climate-control.service';
import { normalizeHvacModes, UNAVAILABLE_HVAC_MODES } from '../../helpers/hvac-modes.helper';
import { IHVACDeviceControl } from '../../interfaces/climate.interface';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AutoScheduleNudgeService } from 'src/app/shared/services/auto-schedule-nudge.service';


interface IHVACDeviceMap {
  [key: string]: IHVACDeviceControl;
}

@Component({
  selector: 'climate-control',
  templateUrl: './climate-control.component.html',
  styleUrls: ['./climate-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClimateControlComponent extends CommonPage implements OnInit {
  public bedroom: RoomName = RoomName.BedroomA;
  public livingroom: RoomName = RoomName.LivingspaceA;

  public hvacDeviceControls: IHVACDeviceMap = {};

  public isSynchroMode: boolean = false;
  public ecoMode: OnOff = OnOff.Off;

  // Active weekday/weekend schedule indicator (VI-275, user story 4): shown on the
  // thermostat so residents see which profile is driving behavior without opening Settings.
  public scheduleSplitOn: boolean = false;
  public activeSchedule: 'weekday' | 'weekend' = 'weekday';

  public currentTempSync?: number;
  public setToTempSync?: number;

  private maxTemp: number = 84;
  private minTemp: number = 65;


  public selectedMode: string = 'off';
  public availableModes: string[] = [];

  private modeChangeTimeout: any;
  private isModeChanging: boolean = false;

  /** Whether the home is reachable for control right now. Drives the
   *  reconnecting/sign-in states and gates taps so we never silently no-op. */
  public controlAvailability: 'ready' | 'reconnecting' | 'needs_reauth' = 'reconnecting';

  /** True when any room already has an Auto Schedule active. Suppresses/retires the nudge. */
  public hasAnyAutoSet: boolean = false;

  /** Guards the nudge against a cold-start race: until the auto_set stream has
   *  emitted at least once, adoption state is unknown, so we don't let a manual
   *  adjustment start an impression (which could briefly surface — and log a
   *  spurious `shown` for — a household that has actually already adopted). */
  private autoSetSeen: boolean = false;

  // Enhanced optimistic update tracking
  private pendingUpdates = new Map<string, any>();
  private updateDebounceTimer: any;
  private tempChangeTimers = new Map<string, any>();

  /** Monotonic id per user gesture. A sync "All" tap fans out to one commit per
   *  zone but shares a single gesture id; the record layer dedups on it so the
   *  gesture counts exactly once — regardless of how many zones ack or when. */
  private gestureCounter = 0;
  private lastRecordedGestureId = 0;
  /** Adjustments committed before the auto_set stream first reported adoption
   *  state, held so they are flushed (not dropped) once it resolves. Capped —
   *  more than the trigger threshold can't change the outcome. */
  private pendingAdjustments = 0;

  /** No HVAC to talk to at all: nothing discovered, or every zone reporting
   *  unavailable. Distinct from isUnavailableSync, which is true when ANY zone
   *  is down and so can't decide whether the whole control is dead. */
  get isHvacUnavailable(): boolean {
    return this.hvacDevices.length === 0
      || this.hvacDevices.every((device: IHVACDeviceControl) => device.isUnavailable);
  }

  /** Modes to render. With no HVAC present the segment would otherwise be
   *  empty, so show the three familiar ones as a disabled placeholder rather
   *  than a blank strip. */
  get displayModes(): string[] {
    return this.isHvacUnavailable ? [...UNAVAILABLE_HVAC_MODES] : this.availableModes;
  }

  get isUnavailableSync(): boolean {
    return this.hvacDevices.map((device: IHVACDeviceControl) => (device.isUnavailable)).reduce((prev, curr) => prev || curr, false);
  }

  get hvacDevices(): IHVACDeviceControl[] {
    return Object.values(this.hvacDeviceControls);
  }

  get currentModeSync() {
    return this.hvacDevices[0]?.currentMode;
  }

  get hvacActionSync() {
    return this.hvacDevices[0]?.hvacAction;
  }

  constructor(
    private ref: ChangeDetectorRef,
    private userSettingsService: UserSettingsService,
    public toastController: ToastController,
    public modalCtrl: ModalController,
    private mqtt: MqttConnectorService,
    private climateControlService: ClimateControlService,
    private analyticsService: AnalyticsService,
    private router: Router,
    private autoScheduleNudge: AutoScheduleNudgeService,
  ) {
    super(toastController, modalCtrl);
  }

  public ngOnInit(): void {

    this.mqtt.getControlAvailability().pipe(
      takeUntil(this.destroy$),
    ).subscribe((availability) => {
      this.controlAvailability = availability;
      this.ref.markForCheck();
    });

    this.userSettingsService.getSyncUnitsSetting().pipe(
      takeUntil(this.destroy$),
    ).subscribe((res) => {
      this.isSynchroMode = res;
      this.ref.markForCheck();
    });


    this.climateControlService.hvacUnits.pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      switchMap((entities: string[]) => {
        if (!entities.length) return of({ messages: [] as (IShadowState | undefined)[], entities: [] as string[] });
        const ids = entities.map(entity => `${actionType.climate}.${entity}`);
        return this.mqtt.listenToEntities(...ids).pipe(
          map(messages => ({ messages, entities })),
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(({ messages, entities }: { messages: (IShadowState | undefined)[], entities: string[] }) => {
      // First, remove any placeholder devices if we have real devices
      const hasRealDevices = messages.some(message => message?.reported?.entity_id);
      if (hasRealDevices) {
        // Remove all placeholder devices
        Object.keys(this.hvacDeviceControls)
          .filter(key => key.startsWith('pending_'))
          .forEach(key => delete this.hvacDeviceControls[key]);
      }
      
      // Track processed devices
      const processedDeviceIds = new Set<string>();
      
      
      messages?.forEach((message: IShadowState | undefined) => {
        if (message?.reported?.entity_id && message?.reported?.attributes) {
          const attributes = message?.reported?.attributes;
          const deviceId = getActionKey(message?.reported?.entity_id);
          
          // If we're in mode changing state, store updates for later
          if (this.isModeChanging) {
            this.pendingUpdates.set(deviceId, message);
            return;
          }
          
          this.updateDevice(deviceId, message);
          // Set reasonable defaults if min/max temps are not provided
          this.minTemp = this.defineNumber(attributes['min_temp']) ?? 65;
          this.maxTemp = this.defineNumber(attributes['max_temp']) ?? 84;
          this.availableModes = normalizeHvacModes(attributes['hvac_modes']);
          processedDeviceIds.add(deviceId);
          

          if (Object.values(this.hvacDeviceControls)?.length) {
            this.currentTempSync = this.isEnabled() ? Object.values(this.hvacDeviceControls).reduce(
              (prevResult: number, device: IHVACDeviceControl) => Math.max(prevResult, device.currentTemp ?? 0), 0
            ) : 0;

            // Check if any device has a pending temperature change
            const hasAnyPendingTempChange = this.hvacDevices.some(device => 
              this.tempChangeTimers.has(device.deviceId)
            );

            // Update setToTempSync from MQTT if no pending changes
            // Otherwise only initialize if undefined
            if (this.setToTempSync === undefined || !hasAnyPendingTempChange) {
              const allDevicesHaveTemp = Object.values(this.hvacDeviceControls).reduce(
                (prevResult: boolean, device: IHVACDeviceControl) => prevResult && !!device.setToTemp, true
              );
              if (allDevicesHaveTemp) {
                // Use the first device's temp (they should be synced in sync mode)
                this.setToTempSync = Object.values(this.hvacDeviceControls)[0].setToTemp;
              } else {
                this.setToTempSync = undefined;
              }
            }
          }
        }
      });
      
      // Add placeholder devices for any entities we haven't processed yet
      entities.forEach((entityId: string, index: number) => {
        if (!processedDeviceIds.has(entityId) && !this.hvacDeviceControls[entityId]) {
          // Create a disabled placeholder device with appropriate names
          let deviceName = 'Climate';
          // Map entity IDs to friendly names
          if (entityId === 'livingspace_a') {
            deviceName = 'Living Room';
          } else if (entityId === 'bedroom_a') {
            deviceName = 'Bedroom';
          } else if (entityId.startsWith('pending_')) {
            deviceName = index === 0 ? 'Bedroom' : 'Living Room';
          }
          
          this.hvacDeviceControls[entityId] = {
            deviceId: entityId,
            deviceName: deviceName,
            currentTemp: undefined,
            setToTemp: undefined,
            currentMode: 'off',
            isUnavailable: true,
            hvacAction: undefined,
            availableModes: []
          };
        }
      });
      
      
      // Debounce UI updates to batch them
      this.debounceUIUpdate();
    });

    this.climateControlService.hvacUnits.pipe(
      switchMap((entities: string[]) => {
        const ids = entities.map(entity => `${actionType.inputBoolean}.${entity}${actionId.ecoMode}`);
        return this.mqtt.listenToEntities(...ids);
      }),
      takeUntil(this.destroy$)
    ).subscribe((messages: (IShadowState | undefined)[]) => {
      const ecoModes = messages?.map((message: IShadowState | undefined) => {
        return <OnOff>message?.reported.state?.toUpperCase();
      })
      this.ecoMode = this.defineOnOff(ecoModes) || OnOff.Off;
    });

    // Active schedule indicator: listen to each zone's split flag + active-schedule
    // sensor (returned in [split, active] pairs, in entity order).
    this.climateControlService.hvacUnits.pipe(
      switchMap((entities: string[]) => {
        if (!entities.length) return of([] as (IShadowState | undefined)[]);
        const ids = entities.flatMap((entity) => [
          `${actionType.inputBoolean}.${entity}${actionId.scheduleSplit}`,
          `${actionType.sensor}.${entity}${actionId.activeSchedule}`,
        ]);
        return this.mqtt.listenToEntities(...ids);
      }),
      takeUntil(this.destroy$)
    ).subscribe((messages: (IShadowState | undefined)[]) => {
      let splitOn = false;
      let active: 'weekday' | 'weekend' = 'weekday';
      for (let i = 0; i < messages.length; i += 2) {
        if (messages[i]?.reported?.state === 'on') splitOn = true;
        if (messages[i + 1]?.reported?.state?.trim() === 'weekend') active = 'weekend';
      }
      this.scheduleSplitOn = splitOn;
      this.activeSchedule = active;
      this.ref.markForCheck();
    });

    // Watch per-room Auto Schedule (auto_set) so the "Adjusting a lot?" nudge knows
    // adoption state — both to suppress it and to retire it permanently on adoption.
    this.climateControlService.hvacUnits.pipe(
      switchMap((entities: string[]) => {
        const ids = entities.map(
          (entity) => `${actionType.inputBoolean}.${entity}${actionId.autoSet}`,
        );
        return this.mqtt.listenToEntities(...ids);
      }),
      map((messages: (IShadowState | undefined)[]) => messages.some((m) => m?.reported?.state === 'on')),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe((hasAnyAutoSet: boolean) => {
      const firstReport = !this.autoSetSeen;
      this.autoSetSeen = true;
      this.hasAnyAutoSet = hasAnyAutoSet;
      void this.autoScheduleNudge.refresh(this.hasAnyAutoSet, this.mqtt.unitId);
      // Flush adjustments deferred during cold start (committed before adoption
      // state was known) now that it has resolved.
      if (firstReport && this.pendingAdjustments > 0) {
        const deferred = this.pendingAdjustments;
        this.pendingAdjustments = 0;
        for (let i = 0; i < deferred; i++) {
          void this.autoScheduleNudge.recordManualAdjustment(this.hasAnyAutoSet, this.mqtt.unitId);
        }
      }
    });
  }

  public isEnabled(device?: IHVACDeviceControl): boolean {
    if (device) {
      return device.currentMode != 'off' && !device.isUnavailable;
    } else {
      // At least one device is enabled, not all need to be enabled
      return this.hvacDevices.some((device: IHVACDeviceControl) => (
        device.currentMode != 'off' && !device.isUnavailable
      ));
    }
  }

  public canIncreaseTemp(device?: IHVACDeviceControl): boolean {
    if (device) {
      return device.currentMode !== 'off' && !device.isUnavailable
              && device.setToTemp !== undefined && device.setToTemp < this.maxTemp;
    }
    return this.hvacDevices.every((device: IHVACDeviceControl) => (
      device.currentMode !== 'off' && !device.isUnavailable
      && device.setToTemp !== undefined && device.setToTemp < this.maxTemp
    ));
  }

  public canDecreaseTemp(device?: IHVACDeviceControl): boolean {
    if (device) {
      return device.currentMode !== 'off' && !device.isUnavailable
              && device.setToTemp !== undefined && device.setToTemp > this.minTemp;
    }
    return this.hvacDevices.every((device: IHVACDeviceControl) => (
      device.currentMode !== 'off' && !device.isUnavailable
      && device.setToTemp !== undefined && device.setToTemp > this.minTemp
    ));
  }

  public isTemperatureControlDisable(device?: IHVACDeviceControl): boolean {
    const canIncrease = this.canIncreaseTemp(device);
    const canDecrease = this.canDecreaseTemp(device);
    const canTempControl = canIncrease || canDecrease;
    
    
    return !canTempControl /* || this.ecoMode === OnOff.On */;
  }

  /**
   * Block a control tap when the home is unreachable and tell the resident why,
   * instead of letting the publish silently no-op. Returns true if controllable.
   */
  private ensureControllable(): boolean {
    if (this.controlAvailability === 'ready') return true;
    if (this.controlAvailability === 'needs_reauth') {
      this.presentToast('Please sign in again to control your home.');
    } else {
      this.presentToast('Reconnecting to your home. Try again in a moment.');
    }
    return false;
  }

  /** Sign-in CTA target for the needs-reauth banner. */
  public goToSignIn(): void {
    this.router.navigate(['/signin']);
  }

  public increaseTemp(device?: IHVACDeviceControl) {
    if (!this.ensureControllable()) return;
    if (!this.canIncreaseTemp(device)) return;

    // One gesture (a per-room tap, or a sync "All" tap that fans out to every
    // zone) gets one id, deduped in onCommittedAdjustment so it counts once.
    const gestureId = ++this.gestureCounter;
    if (device) {
      const newTemp = Math.min(this.maxTemp, (device.setToTemp || device.currentTemp || 72) + 1);
      this.hvacDeviceControls[device.deviceId].setToTemp = newTemp;
      this.scheduleTempUpdate(device.deviceId, newTemp, gestureId);
    } else {
      // Sync mode
      const newTemp = Math.min(this.maxTemp, (this.setToTempSync || this.currentTempSync || 72) + 1);
      this.setToTempSync = newTemp;
      this.hvacDevices.forEach((device: IHVACDeviceControl) => {
        this.hvacDeviceControls[device.deviceId].setToTemp = newTemp;
        this.scheduleTempUpdate(device.deviceId, newTemp, gestureId);
      });
    }
    this.ref.markForCheck();
  }

  public decreaseTemp(device?: IHVACDeviceControl) {
    if (!this.ensureControllable()) return;
    if (!this.canDecreaseTemp(device)) return;

    const gestureId = ++this.gestureCounter;
    if (device) {
      const newTemp = Math.max(this.minTemp, (device.setToTemp || device.currentTemp || 72) - 1);
      this.hvacDeviceControls[device.deviceId].setToTemp = newTemp;
      this.scheduleTempUpdate(device.deviceId, newTemp, gestureId);
    } else {
      // Sync mode
      const newTemp = Math.max(this.minTemp, (this.setToTempSync || this.currentTempSync || 72) - 1);
      this.setToTempSync = newTemp;
      this.hvacDevices.forEach((device: IHVACDeviceControl) => {
        this.hvacDeviceControls[device.deviceId].setToTemp = newTemp;
        this.scheduleTempUpdate(device.deviceId, newTemp, gestureId);
      });
    }
    this.ref.markForCheck();
  }

  private scheduleTempUpdate(deviceId: string, temperature: number, gestureId: number): void {
    // Clear any existing timer for this device
    if (this.tempChangeTimers.has(deviceId)) {
      clearTimeout(this.tempChangeTimers.get(deviceId));
    }

    // Set a new timer with 2 second delay
    const timer = setTimeout(() => {
      // Track temperature change after debounce settles
      this.analyticsService.trackWidgetInteraction('thermostat', 'temperature_change', { newTemp: temperature });
      // Weekend override proxy (VI-275): count weekend-day overrides separately.
      if (this.isWeekendNow()) {
        this.analyticsService.trackWidgetInteraction('thermostat', 'temperature_change_weekend', { newTemp: temperature });
      }

      this.climateControlService.setTemperature(deviceId, temperature).subscribe({
        next: () => {
          this.tempChangeTimers.delete(deviceId);
          // Only a change that actually reached the home counts as a manual
          // adjustment for the nudge — a rejected/unreachable publish does not.
          this.onCommittedAdjustment(gestureId);
        },
        error: (err) => {
          this.tempChangeTimers.delete(deviceId);
          // The publish never left the phone (or was rejected). Tell the user
          // rather than leaving an optimistic value that looks applied.
          this.presentToast("Couldn't reach your home. Your temperature change was not saved.");
          this.ref.markForCheck();
        }
      });
    }, 2000);

    this.tempChangeTimers.set(deviceId, timer);
  }

  /**
   * One settled, home-confirmed manual temperature change → record it for the
   * Auto Schedule nudge. Called once per zone that acks; deduped by `gestureId`
   * so a sync "All" gesture (many zone acks, any timing) counts exactly once.
   */
  private onCommittedAdjustment(gestureId: number): void {
    if (gestureId === this.lastRecordedGestureId) return;
    this.lastRecordedGestureId = gestureId;
    // Defer rather than drop until adoption state is known — flushed by the
    // auto_set subscription's first report.
    if (!this.autoSetSeen) {
      if (this.pendingAdjustments < 3) this.pendingAdjustments++;
      return;
    }
    void this.autoScheduleNudge.recordManualAdjustment(this.hasAnyAutoSet, this.mqtt.unitId);
  }


  // Calendar weekend (Sat/Sun) — the active-schedule boundary is calendar-based (VI-275).
  private isWeekendNow(): boolean {
    const d = new Date().getDay();
    return d === 0 || d === 6;
  }

  public onModeChange(event: any) {
    const mode = event.detail.value;

    // The actual current mode, captured before any optimistic mutation, so we
    // can roll the segment back if the home is unreachable or the publish fails.
    const actualMode = this.currentModeSync ?? this.hvacDevices[0]?.currentMode ?? 'off';

    if (!this.ensureControllable()) {
      this.selectedMode = actualMode;
      this.ref.markForCheck();
      return;
    }

    this.analyticsService.trackWidgetInteraction('thermostat', 'mode_change', { mode });
    if (this.isWeekendNow()) {
      this.analyticsService.trackWidgetInteraction('thermostat', 'mode_change_weekend', { mode });
    }

    this.isModeChanging = true;

    // Clear any existing timeout
    if (this.modeChangeTimeout) {
      clearTimeout(this.modeChangeTimeout);
    }

    // Snapshot per-device modes for rollback on failure.
    const prevModes = new Map(this.hvacDevices.map((d) => [d.deviceId, d.currentMode]));

    // Set temporary state
    this.hvacDevices.forEach((device) => this.hvacDeviceControls[device.currentMode = mode]);

    this.ref.markForCheck();


    combineLatest(
      this.hvacDevices.map((device) => this.climateControlService.setMode(device.deviceId, mode))
    ).subscribe({
      next: () => {
        // Set timeout to allow MQTT updates after 2 seconds
        this.modeChangeTimeout = setTimeout(() => {
          this.isModeChanging = false;
          // Apply any pending updates that arrived during mode change
          this.applyPendingUpdates();
          this.ref.markForCheck();
        }, 2000);
      },
      error: () => {
        // Publish failed (home unreachable). Roll back the optimistic mode and
        // segment so the UI matches reality instead of showing a phantom change.
        this.isModeChanging = false;
        this.hvacDevices.forEach((device) => {
          const prev = prevModes.get(device.deviceId);
          if (prev !== undefined) device.currentMode = prev;
        });
        this.selectedMode = actualMode;
        this.presentToast("Couldn't reach your home. The mode change was not saved.");
        this.ref.markForCheck();
      },
    });
  }

  public getModeLabel(mode?: string): string {
    switch(mode) {
      case 'off': return 'Off';
      case 'heat': return 'Heat';
      case 'cool': return 'Cool';
      case 'heat_cool': return 'Auto';
      default: return 'Off';
    }
  }

  private updateDevice(deviceId: string, deviceState: IShadowState) {
    const attributes = deviceState?.reported?.attributes;
    if (!attributes) return;

    const device: Partial<IHVACDeviceControl> | null = this.climateControlService.getDeviceBaseData(deviceId, deviceState);
    if (!device) return


    const existsDevice = this.hvacDeviceControls[deviceId] ?? undefined;

    if (!this.isModeChanging) {
      device.currentMode = deviceState.reported.state || 'off';
    }

    // Set available modes
    if (!existsDevice) {
      device.availableModes = normalizeHvacModes(attributes['hvac_modes']);
    }

    // Ensure setToTemp is initialized with the current temperature setting
    // Only preserve user changes if they're still pending (awaiting MQTT confirmation)
    const mqttTemp = device.setToTemp; // Already calculated from getDeviceBaseData (handles temperature or target_temp_low)
    const hasPendingTempChange = this.tempChangeTimers.has(deviceId);
    
    if (mqttTemp === undefined) {
      // If no MQTT temp available, keep existing or use undefined
      device.setToTemp = existsDevice?.setToTemp;
    } else if (existsDevice && existsDevice.setToTemp !== undefined && hasPendingTempChange) {
      // Preserve the user's temperature change only if it's still pending
      device.setToTemp = existsDevice.setToTemp;
    } else {
      // Update from MQTT if no pending change or if pending change has completed
      device.setToTemp = mqttTemp;
    }

    this.selectedMode = device.currentMode!;

    this.hvacDeviceControls[deviceId] = {
      ...(existsDevice ?? {}),
      ...device
    }
  }

  private debounceUIUpdate(): void {
    // Clear existing timer
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }
    
    // Set new timer to batch updates
    this.updateDebounceTimer = setTimeout(() => {
      this.ref.markForCheck();
    }, 50); // 50ms debounce
  }
  
  private applyPendingUpdates(): void {
    // Apply any updates that were queued during mode change
    this.pendingUpdates.forEach((message, deviceId) => {
      this.updateDevice(deviceId, message);
    });
    
    // Clear pending updates
    this.pendingUpdates.clear();
    
    // Update UI
    this.ref.markForCheck();
  }

  ngOnDestroy() {
    if (this.modeChangeTimeout) {
      clearTimeout(this.modeChangeTimeout);
    }
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }
    // Clear all temperature change timers
    this.tempChangeTimers.forEach((timer) => clearTimeout(timer));
    this.tempChangeTimers.clear();
    super.ngOnDestroy();
  }
}
